import * as Yup from 'yup';
import { CREDIT_NOTE_DOCUMENT_TYPE_CODE, DEFAULT_CURRENCY_CODE } from '../constants/defaults';
import { DEFAULT_VALIDATION_MESSAGES } from '../hooks/useTranslatedMessages';
import { getDocumentNoteSchema } from './documentNoteValidation';
import { isOutOfScopeDocumentType } from '../utils/documentTypeRules';
import { isMarginScheme } from '../utils/helper';
import { createAllowanceChargeSchema } from './allowanceChargeValidation';
import { sellerBuyerDistinctnessTest } from './partyDistinctnessValidation';
import {
  createProfileTransactionTypeFields,
  withAgentBillingPartyDistinctness,
} from './transactionTypeProfileValidation';
import { standardRateAdditionalTaxRatePositiveTest } from './taxCategoryValidation';

const defaultMessages = DEFAULT_VALIDATION_MESSAGES;

const getRootValue = (context) => {
  if (context.from && Array.isArray(context.from) && context.from.length > 0) {
    return context.from[context.from.length - 1].value;
  }

  return context.parent || {};
};

export const getCreditNoteValidationSchema = (messages = defaultMessages, { isPurchase = false } = {}) => {
  const m = messages.credit_note || defaultMessages.credit_note;
  const transactionMessages = messages.invoice || defaultMessages.invoice;
  const allowanceChargeMessages = messages.allowance_charge || defaultMessages.allowance_charge;
  const lineItemMessages = messages.line_item || defaultMessages.line_item;
  const documentLevelAllowanceSchema = createAllowanceChargeSchema(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
    'ALLOWANCE',
    false,
    false,
    true,
    null,
    allowanceChargeMessages,
  );
  const documentLevelChargeSchema = createAllowanceChargeSchema(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
    'CHARGE',
    false,
    false,
    true,
    null,
    allowanceChargeMessages,
  );

  return Yup.object({
    credit_note_type_code: Yup.string().required(m.credit_note_type_required),
    profile_execution_id: Yup.string()
      .required(m.transaction_types_required)
      .test('is-valid-transaction-types', m.invalid_transaction_type, function (value) {
        if (!value) return false;

        return value.length === 8 && value.split('').every((char) => char === '0' || char === '1');
      })
      .test('out-of-scope-transaction-restrictions', m.invalid_transaction_type_for_out_of_scope, function (value) {
        if (!value) return true;
        const creditNoteTypeCode = this.parent?.credit_note_type_code;
        if (!isOutOfScopeDocumentType(creditNoteTypeCode)) return true;

        return value[1] !== '1' && value[2] !== '1' && value[3] !== '1';
      }),

    document_number: Yup.string().when('$auto_numbering_enabled', {
      is: false,
      then: (schema) =>
        schema.required(m.document_number_required).test('log-debug', 'DEBUG', function (value) {
          return true;
        }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    issue_date: Yup.date().required(m.credit_note_date_required),

    note: getDocumentNoteSchema(messages, 'credit_note'),

    invoice_reference_id: Yup.array().when('discrepancy_response', {
      is: (val) => val === 'VD',
      then: (schema) =>
        schema.test('vd-not-allowed', m.invoice_reference_not_allowed_for_vd, (value) => {
          const refs = value || [];
          return refs.filter((ref) => ref && ref.trim() !== '').length === 0;
        }),
      otherwise: (schema) => schema.of(Yup.string().trim().required()).min(1, m.invoice_reference_id_required),
    }),

    invoice_reference_date: Yup.date().when('discrepancy_response', {
      is: (val) => val === 'VD',
      then: (schema) =>
        schema.nullable().test('vd-not-allowed', m.invoice_reference_date_not_allowed_for_vd, (value) => value == null),
      otherwise: (schema) => schema.required(m.invoice_reference_date_required),
    }),
    discrepancy_response: Yup.string().required(m.discrepancy_response_required),

    accounting_supplier_party: withAgentBillingPartyDistinctness(Yup.mixed(), m, isPurchase)
      .required(m.billed_by_required)
      .test('billed-by-vat-for-commercial', m.billed_by_vat_required_for_commercial, function (value) {
        if (!value) return true;

        const creditNoteTypeCode = this.parent?.credit_note_type_code;
        if (creditNoteTypeCode !== CREDIT_NOTE_DOCUMENT_TYPE_CODE) {
          return true;
        }

        const companyId = value?.party_tax_scheme?.company_id;
        const hasCompanyId = typeof companyId === 'string' && companyId.trim() !== '';

        return hasCompanyId;
      })
      .test('billed-by-vat-for-purchase', m.supplier_vat_required_for_purchase, function (value) {
        if (!isPurchase || !value) {
          return true;
        }

        const companyId = value?.party_tax_scheme?.company_id;

        return typeof companyId === 'string' && companyId.trim() !== '';
      })
      .test('seller-buyer-distinct', m.seller_buyer_same_entity, sellerBuyerDistinctnessTest()),

    accounting_customer_party: Yup.mixed()
      .required(m.billed_to_required)
      .test('seller-buyer-distinct', m.seller_buyer_same_entity, sellerBuyerDistinctnessTest()),

    document_lines: Yup.array()
      .of(
        Yup.object().shape({
          classified_tax_category: Yup.object().shape({
            id: Yup.string()
              .nullable()
              .required(lineItemMessages.tax_category_required)
              .test(
                'out-of-scope-tax-categories-only',
                lineItemMessages.out_of_scope_allowed_tax_categories,
                function (value) {
                  const root = getRootValue(this);
                  if (!isOutOfScopeDocumentType(root?.credit_note_type_code)) return true;

                  return ['E', 'O', 'Z'].includes(value || '');
                },
              )
              .test(
                'margin-scheme-tax-categories-only',
                lineItemMessages.margin_scheme_allowed_tax_categories,
                function (value) {
                  const root = getRootValue(this);
                  if (!isMarginScheme(root?.profile_execution_id)) return true;

                  return value === 'N';
                },
              ),
            percent: Yup.number()
              .transform((value) => (isNaN(value) ? undefined : value))
              .when('id', {
                is: (id) => id === 'E' || id === 'O',
                then: (schema) => schema.notRequired().default(0),
                otherwise: (schema) => schema.required(lineItemMessages.tax_rate_required),
              })
              .min(0, lineItemMessages.tax_rate_min_zero)
              .max(100, lineItemMessages.tax_rate_max_hundred)
              .test(
                'out-of-scope-tax-rate-zero',
                lineItemMessages.out_of_scope_tax_rate_must_be_zero,
                function (value) {
                  const root = getRootValue(this);
                  if (!isOutOfScopeDocumentType(root?.credit_note_type_code)) return true;

                  return Number(value) === 0;
                },
              )
              .test(
                'standard-rate-additional-tax-rate-positive',
                lineItemMessages.standard_rate_additional_tax_rate_must_be_positive,
                standardRateAdditionalTaxRatePositiveTest,
              ),
          }),
        }),
      )
      .ensure()
      .min(1, m.at_least_one_line_item_required)
      .required(m.line_items_required),

    document_currency_code: Yup.string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .required(m.currency_code_required),

    tax_exchange_rate: Yup.object({
      calculation_rate: Yup.number()
        .nullable()
        .transform((_, val) => (val === '' ? null : Number(val)))
        .when('document_currency_code', {
          is: (code) => code !== DEFAULT_CURRENCY_CODE,
          then: (schema) => schema.required(m.must_be_valid_number).moreThan(0, m.must_be_greater_than_zero),
          otherwise: (schema) => schema.notRequired(),
        }),
    }).nullable(),

    charges: Yup.array().of(documentLevelChargeSchema).ensure().notRequired(),

    allowances: Yup.array().of(documentLevelAllowanceSchema).ensure().notRequired(),

    ...createProfileTransactionTypeFields(transactionMessages, { isPurchase }),
  });
};

export const CreditNotesValidation = getCreditNoteValidationSchema();

export default CreditNotesValidation;
